import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import type { CreateReceiptBundleJobDto } from '@trellis/api'

import { pushEvent } from '@/lib/analytics'
import type {
  AgedReceivablesParams,
  AgingParams,
  BasSummaryParams,
  ClientReportParams,
  ExpenseReportParams,
  ExportReportOptions,
  InvoiceSummaryParams,
  LineItemReportParams,
  ProfitAndLossParams,
  ReceiptBundleDateRange,
  RevenueByTagParams,
  RevenueTimelineParams,
  TaxReportParams,
} from '@/requests/reports'
import {
  createEofyPackWithReceiptsJob,
  createReceiptBundleJob,
  exportEofyPack,
  exportReport,
  getAgedReceivables,
  getAging,
  getBasSummary,
  getClientReport,
  getExpenseReport,
  getInvoiceSummary,
  getLineItemReport,
  getProfitAndLoss,
  getReceiptBundleEstimate,
  getRevenueByTag,
  getRevenueTimeline,
  getTaxReport,
  getTurnover,
} from '@/requests/reports'

export function useInvoiceSummaryQuery(params: InvoiceSummaryParams = {}) {
  return useQuery({
    queryKey: ['reports', 'invoices', params],
    queryFn: () => getInvoiceSummary(params),
    staleTime: 120_000,
  })
}

export function useClientReportQuery(params: ClientReportParams = {}) {
  return useQuery({
    queryKey: ['reports', 'clients', params],
    queryFn: () => getClientReport(params),
    staleTime: 120_000,
  })
}

export function useLineItemReportQuery(params: LineItemReportParams = {}) {
  return useQuery({
    queryKey: ['reports', 'line-items', params],
    queryFn: () => getLineItemReport(params),
    staleTime: 120_000,
  })
}

export function useRevenueTimelineQuery(params: RevenueTimelineParams = {}) {
  return useQuery({
    queryKey: ['reports', 'revenue-timeline', params],
    queryFn: () => getRevenueTimeline(params),
  })
}

export function useAgingQuery(params: AgingParams = {}) {
  return useQuery({
    queryKey: ['reports', 'aging', params],
    queryFn: () => getAging(params),
    staleTime: 60_000,
  })
}

export function useRevenueByTagQuery(params: RevenueByTagParams = {}) {
  return useQuery({
    queryKey: ['reports', 'revenue-by-tag', params],
    queryFn: () => getRevenueByTag(params),
  })
}

export function useExpenseReportQuery(params: ExpenseReportParams) {
  return useQuery({
    queryKey: ['reports', 'expenses', params],
    queryFn: () => getExpenseReport(params),
    enabled: !!params.startDate && !!params.endDate,
  })
}

export function useTaxReportQuery(params: TaxReportParams) {
  return useQuery({
    queryKey: ['reports', 'tax', params],
    queryFn: () => getTaxReport(params),
    enabled: !!params.startDate && !!params.endDate,
  })
}

export function useBasSummaryQuery(params: BasSummaryParams) {
  return useQuery({
    queryKey: ['reports', 'bas-summary', params],
    queryFn: () => getBasSummary(params),
    enabled: !!params.startDate && !!params.endDate,
  })
}

export function useProfitAndLossQuery(params: ProfitAndLossParams) {
  return useQuery({
    queryKey: ['reports', 'profit-and-loss', params],
    queryFn: () => getProfitAndLoss(params),
    enabled: !!params.startDate && !!params.endDate,
  })
}

export function useTurnoverQuery() {
  return useQuery({
    queryKey: ['reports', 'turnover'],
    queryFn: () => getTurnover(),
  })
}

/**
 * Fetches buckets + cursor-paginated detail rows together — the detail table starts
 * collapsed and reuses this same query's already-fetched first page rather than issuing a
 * second request on expand.
 */
export function useAgedReceivablesQuery(
  params: Omit<AgedReceivablesParams, 'cursor'> = {},
) {
  return useInfiniteQuery({
    queryKey: ['reports', 'aged-receivables', params],
    queryFn: ({ pageParam }) => getAgedReceivables({ ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.data?.nextCursor ?? undefined,
  })
}

export function useReportExportMutation() {
  return useMutation({
    mutationFn: (options: ExportReportOptions) => exportReport(options),
    onSuccess: (_, options) =>
      pushEvent('data_export_requested', { export_type: options.reportKey }),
  })
}

export function useEofyPackExportMutation() {
  return useMutation({
    mutationFn: (financialYear: string) => exportEofyPack(financialYear),
    onSuccess: () => pushEvent('data_export_requested', { export_type: 'eofy-pack' }),
  })
}

/** Cheap pre-commit size estimate shown before a receipt bundle job is created. */
export function useReceiptBundleEstimateQuery(params: ReceiptBundleDateRange) {
  return useQuery({
    queryKey: ['reports', 'receipt-bundle-estimate', params],
    queryFn: () => getReceiptBundleEstimate(params),
    enabled: !!params.startDate && !!params.endDate,
  })
}

export function useCreateReceiptBundleJobMutation() {
  return useMutation({
    mutationFn: async (body: CreateReceiptBundleJobDto) => {
      const result = await createReceiptBundleJob(body)
      if (result.error) throw new Error(result.error.message)
      return result.data
    },
  })
}

/** Starts the combined EOFY-pack-with-receipts job — the "include receipts" checkbox path. */
export function useCreateEofyPackWithReceiptsJobMutation() {
  return useMutation({
    mutationFn: async (financialYear: string) => {
      const result = await createEofyPackWithReceiptsJob(financialYear)
      if (result.error) throw new Error(result.error.message)
      return result.data
    },
  })
}
